/*
 * Cart drawer: adds products with AJAX, opens the cart as a sidebar instead of going to /cart
 * and loads upsell products for the most recently added item.
 * Markup: snippets/cart-drawer.liquid + snippets/cart-drawer-contents.liquid
 */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.upsell = this.querySelector("[data-cart-upsell]");
    this.upsellCache = {};

    this.addEventListener("click", this.onClick.bind(this));
    document.addEventListener("keydown", this.onKeyDown.bind(this));

    document.addEventListener("click", (event) => {
      const trigger = event.target.closest("[data-cart-drawer-open]");
      if (!trigger) {
        return;
      }

      event.preventDefault();
      this.open(trigger);
    });

    document.addEventListener("submit", this.onFormSubmit.bind(this));

    // keep the drawer in sync when the cart is changed elsewhere (e.g. the /cart page)
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source !== "cart-drawer") {
        this.refresh();
      }
    });

    // the page can come back from the back/forward cache with an outdated cart
    window.addEventListener("pageshow", (event) => {
      if (event.persisted) {
        this.refresh();
      }
    });
  }

  get isOpen() {
    return this.hasAttribute("data-open");
  }

  open(trigger) {
    this.previouslyFocused = trigger || document.activeElement;
    this.setAttribute("data-open", "");
    document.body.style.overflow = "hidden";

    this.loadUpsell();

    // wait for the visibility transition before moving focus into the drawer
    setTimeout(() => {
      this.querySelector("[data-cart-drawer-close][aria-label]")?.focus();
    }, 50);
  }

  close() {
    this.removeAttribute("data-open");
    document.body.style.overflow = "";
    this.previouslyFocused?.focus?.();
  }

  onClick(event) {
    if (event.target.closest("[data-cart-drawer-close]")) {
      event.preventDefault();
      this.close();
      return;
    }

    const quantityButton = event.target.closest("[data-cart-drawer-quantity]");
    if (quantityButton) {
      event.preventDefault();
      this.changeLine(
        parseInt(quantityButton.dataset.line),
        parseInt(quantityButton.dataset.cartDrawerQuantity)
      );
    }
  }

  onKeyDown(event) {
    if (!this.isOpen) {
      return;
    }

    if (event.key === "Escape") {
      this.close();
      return;
    }

    if (event.key !== "Tab") {
      return;
    }

    // keep keyboard focus inside the drawer
    const focusable = [
      ...this.querySelectorAll("a[href], button:not([disabled])"),
    ].filter((el) => el.offsetParent !== null);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  async onFormSubmit(event) {
    const form = event.target;
    if (!/\/cart\/add\/?$/.test(form.getAttribute("action") || "")) {
      return;
    }

    event.preventDefault();

    const button = event.submitter || form.querySelector('[type="submit"]');
    const buttonHTML = button?.innerHTML;
    if (button) {
      button.setAttribute("disabled", true);
      button.innerHTML = `<svg class="animate-spin h-5 w-5 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <span>Toevoegen...</span>`;
    }

    const formData = new FormData(form);
    formData.append("sections", "cart-drawer");
    formData.append("sections_url", window.location.pathname);

    try {
      const response = await fetch(`${routes.cart_add_url}.js`, {
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/javascript",
        },
        body: formData,
      });
      const data = await response.json();

      if (data.status) {
        showCartToast(data.description || data.message || cartStrings.error);
        return;
      }

      this.renderContents(data.sections["cart-drawer"]);
      publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-drawer" });
      this.open(button);
    } catch (error) {
      console.error(error);
      showCartToast(cartStrings.error);
    } finally {
      if (button) {
        button.innerHTML = buttonHTML;
        button.removeAttribute("disabled");
      }
    }
  }

  async changeLine(line, quantity) {
    const inner = this.querySelector("[data-cart-drawer-inner]");
    inner.classList.add("opacity-50", "pointer-events-none");

    try {
      const response = await fetch(`${routes.cart_change_url}.js`, {
        ...fetchConfig(),
        body: JSON.stringify({
          line,
          quantity,
          sections: ["cart-drawer"],
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();

      if (data.status) {
        showCartToast(data.description || data.message || cartStrings.error);
        inner.classList.remove("opacity-50", "pointer-events-none");
        return;
      }

      this.renderContents(data.sections["cart-drawer"]);
      this.loadUpsell();
      publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-drawer" });
    } catch (error) {
      console.error(error);
      showCartToast(cartStrings.error);
      inner.classList.remove("opacity-50", "pointer-events-none");
    }
  }

  async refresh() {
    try {
      const response = await fetch(`${routes.root_url}?sections=cart-drawer`);
      const sections = await response.json();
      this.renderContents(sections["cart-drawer"]);
      if (this.isOpen) {
        this.loadUpsell();
      }
    } catch (error) {
      console.error(error);
    }
  }

  renderContents(sectionHTML) {
    const html = new DOMParser().parseFromString(sectionHTML, "text/html");
    const source = html.querySelector("[data-cart-drawer-inner]");
    const target = this.querySelector("[data-cart-drawer-inner]");
    if (!source || !target) {
      return;
    }

    const focused = target.contains(document.activeElement)
      ? document.activeElement
      : null;
    target.replaceWith(source);
    this.fillUpsell();

    // the focused button was replaced, move focus to the same button in the new contents
    if (focused && this.isOpen) {
      const sameButton = source.querySelector(
        `[data-line="${focused.dataset.line}"][data-cart-drawer-action="${focused.dataset.cartDrawerAction}"]:not([disabled])`
      );
      (
        sameButton ||
        source.querySelector("[data-cart-drawer-close][aria-label]")
      )?.focus();
    }

    const count = source.dataset.cartCount;
    document.querySelectorAll("[data-cart-count-bubble]").forEach((bubble) => {
      bubble.textContent = count;
    });
  }

  async loadUpsell() {
    const items = [...this.querySelectorAll("[data-cart-drawer-item]")];
    if (items.length === 0) {
      this.upsellProducts = null;
      this.fillUpsell();
      return;
    }

    // the newest line item is always first; the list changes when the cart contents change
    const productId = items[0].dataset.productId;
    const cacheKey = items.map((item) => item.dataset.productId).join(",");
    if (this.upsellKey === cacheKey) {
      return;
    }
    this.upsellKey = cacheKey;

    const products =
      (await this.fetchUpsell(productId, "complementary")) ||
      (await this.fetchUpsell(productId, "related"));

    if (this.upsellKey !== cacheKey) {
      return;
    }

    this.upsellProducts = products;
    this.fillUpsell();
  }

  async fetchUpsell(productId, intent) {
    const url = `${routes.product_recommendations_url}?section_id=cart-drawer-upsell&product_id=${productId}&limit=6&intent=${intent}`;

    try {
      if (!this.upsellCache[url]) {
        this.upsellCache[url] = fetch(url).then((response) => response.text());
      }
      const html = new DOMParser().parseFromString(
        await this.upsellCache[url],
        "text/html"
      );
      const products = html.querySelector("[data-cart-upsell-products]");

      return products && products.children.length > 0 ? products : null;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  // fills both the desktop column and the mobile row (which is re-rendered with every cart change)
  fillUpsell() {
    const products = this.upsellProducts;
    const mobile = this.querySelector("[data-cart-upsell-mobile]");

    [this.upsell, mobile].forEach((container) => {
      if (!container) {
        return;
      }

      if (!products) {
        container.removeAttribute("data-loaded");
        container.classList.add("hidden");
        return;
      }

      container.querySelector("[data-cart-upsell-title]").textContent =
        products.dataset.title;
      container.querySelector("[data-cart-upsell-list]").innerHTML =
        products.innerHTML;
      container.setAttribute("data-loaded", "");
      if (container === mobile) {
        container.classList.remove("hidden");
      }
    });
  }
}

customElements.define("cart-drawer", CartDrawer);

function showCartToast(message) {
  const portal = document.getElementById("toasts");
  if (!portal) {
    return;
  }

  const toast = document.createElement("div");
  toast.setAttribute("role", "alert");
  toast.className =
    "fixed left-1/2 -translate-x-1/2 bottom-24 sm:bottom-8 z-[80] max-w-[90vw] bg-black text-white text-sm px-5 py-3 rounded-full shadow-2xl transition-opacity duration-300";
  toast.textContent = message;
  portal.appendChild(toast);

  setTimeout(() => {
    toast.classList.add("opacity-0");
    setTimeout(() => toast.remove(), 300);
  }, 4000);
}
